import { signInAnonymously, signOut } from 'firebase/auth';
import type { Story, StoriesResponse, UserProfileResponse } from '@murmurations/core';
import { auth, startCustomTrace } from '../firebase/init';

// Empty string = relative URLs, so requests go to whatever host served the page.
// In dev the Vite proxy forwards /api/* to localhost:9002.
// In production set VITE_API_BASE_URL to the deployed App Hosting URL if the
// AR frontend and API are on different domains; leave unset if Firebase Hosting
// rewrites /api/** to the backend.
const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
const VIEWED_STORIES_KEY_PREFIX = 'murmurations:viewed-story-ids:';
const DELETION_CODE_LENGTH = 36;

function hasRecordedViewThisSession(uid: string, storyId: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const key = `${VIEWED_STORIES_KEY_PREFIX}${uid}`;
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return false;
    const viewedStoryIds = JSON.parse(raw) as string[];
    return Array.isArray(viewedStoryIds) && viewedStoryIds.includes(storyId);
  } catch {
    return false;
  }
}

function markViewRecordedThisSession(uid: string, storyId: string): void {
  if (typeof window === 'undefined') return;
  try {
    const key = `${VIEWED_STORIES_KEY_PREFIX}${uid}`;
    const raw = window.sessionStorage.getItem(key);
    const viewedStoryIds = raw ? (JSON.parse(raw) as string[]) : [];
    if (!Array.isArray(viewedStoryIds) || viewedStoryIds.includes(storyId)) return;
    viewedStoryIds.push(storyId);
    window.sessionStorage.setItem(key, JSON.stringify(viewedStoryIds));
  } catch {
    // Best-effort only: storage failures should never affect playback.
  }
}

/**
 * Returns a Firebase ID token for the current (or freshly created anonymous)
 * user. Both the story read and the recording submission paths call this.
 */
export async function getFirebaseIdToken(): Promise<string> {
  let user = auth.currentUser;
  if (!user) {
    const cred = await signInAnonymously(auth);
    user = cred.user;
  }
  return user.getIdToken();
}

/**
 * Mints a BRAND-NEW anonymous identity for a single story submission and returns
 * its ID token. Call this exactly once at the start of each submission — not for
 * reads.
 *
 * Why per-submission: the submitter email shown to moderators is not stored on
 * the story; it is resolved at read time by a reverse lookup
 * (`users where uids array-contains submittedByUid`). Firebase persists a single
 * anonymous UID per browser, and the email path welds that UID into an
 * email-keyed `users` doc via `arrayUnion` and never detaches it. Reusing the UID
 * across submissions therefore (a) leaks a stale email onto later anonymous
 * stories and (b) misroutes a story submitted with a different email to whichever
 * email doc the `limit(1)` lookup happens to return first.
 *
 * Signing out first is required: `signInAnonymously` returns the *existing*
 * anonymous user when one is already signed in, so without the sign-out we'd keep
 * the same UID. With a fresh UID each submission maps to exactly one email — or to
 * none, for anonymous submissions — making the reverse lookup unambiguous. When
 * the chosen email already exists in `users`, the new UID simply joins that doc's
 * `uids` array (the email-keyed record is reused; only the UID is new).
 */
export async function startFreshSubmissionSession(): Promise<string> {
  if (auth.currentUser) {
    await signOut(auth);
  }
  const cred = await signInAnonymously(auth);
  return cred.user.getIdToken();
}

/**
 * Fetches all approved stories from the public feed endpoint.
 * The API already sorts by starRating DESC then createdAt DESC.
 * Returns stories as-is; the caller filters by audioCleanupStatus.
 */
export async function fetchApprovedStories(): Promise<Story[]> {
  const fetchApprovedStoriesTrace = startCustomTrace('ar_fetch_approved_stories');
  try {
    const token = await getFirebaseIdToken();
    const res = await fetch(`${BASE_URL}/api/stories`, {
      headers: { Authorization: ['Bearer', token].join(' ') },
    });
    if (!res.ok) throw new Error(`GET /api/stories failed: ${res.status}`);
    const data = (await res.json()) as StoriesResponse;
    return data.stories;
  } finally {
    fetchApprovedStoriesTrace?.stop();
  }
}

/**
 * Records a view for a story, incrementing its `views` counter by one.
 *
 * Best-effort and fire-and-forget: a failure here must never interrupt
 * playback, so the caller should not await this in a way that blocks the UI
 * and we swallow errors with a warning. Session dedup is enforced per
 * authenticated Firebase uid via sessionStorage to prevent refresh spamming.
 */
export async function recordStoryView(storyId: string): Promise<void> {
  const recordStoryViewTrace = startCustomTrace('ar_record_story_view');
  try {
    const token = await getFirebaseIdToken();
    const uid = auth.currentUser?.uid;
    if (uid && hasRecordedViewThisSession(uid, storyId)) {
      return;
    }
    const res = await fetch(`${BASE_URL}/api/stories/${encodeURIComponent(storyId)}/view`, {
      method: 'POST',
      headers: { Authorization: ['Bearer', token].join(' ') },
    });
    if (!res.ok) {
      console.warn(`[storiesClient] POST /api/stories/${storyId}/view failed: ${res.status}`);
      return;
    }
    if (uid) {
      markViewRecordedThisSession(uid, storyId);
    }
  } catch (err) {
    console.warn(`[storiesClient] recordStoryView(${storyId}) failed:`, err);
  } finally {
    recordStoryViewTrace?.stop();
  }
}

/**
 * Associates an email address with the current user's profile.
 * Called best-effort during submission when the user opts into email
 * notification; failure is logged but does not block the upload.
 */
export async function registerUser(
  idToken: string,
  email?: string,
): Promise<UserProfileResponse['profile']> {
  const registerUserTrace = startCustomTrace('ar_register_user');
  try {
    const body = JSON.stringify(email !== undefined ? { email } : {});
    const res = await fetch(`${BASE_URL}/api/users/me`, {
      method: 'POST',
      headers: {
        Authorization: ['Bearer', idToken].join(' '),
        'Content-Type': 'application/json',
      },
      body,
    });
    if (!res.ok) throw new Error(`POST /api/users/me failed: ${res.status}`);
    const data = (await res.json()) as UserProfileResponse;
    return data.profile;
  } finally {
    registerUserTrace?.stop();
  }
}

export async function deleteStoryByCode(deletionCode: string): Promise<{ success: true; storyId: string }> {
  const normalizedCode = deletionCode.trim().toLowerCase();
  if (normalizedCode.length !== DELETION_CODE_LENGTH) {
    throw new Error('Deletion code must be 36 characters.');
  }

  const token = await getFirebaseIdToken();
  const res = await fetch(`${BASE_URL}/api/stories/delete`, {
    method: 'POST',
    headers: {
      Authorization: 'Bearer ' + token,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ deletionCode: normalizedCode }),
  });

  if (!res.ok) {
    const payload = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? `POST /api/stories/delete failed: ${res.status}`);
  }

  return (await res.json()) as { success: true; storyId: string };
}
